import {
  APP_LABELS,
  APP_TAG_COLORS,
  DEFAULT_LOCALE,
  LOCALES,
  appParam,
  parseApp,
  parseTypeSlug,
  typeLabel,
  typeSlug,
} from './legalConstants';
import { Alert, Box, Button, PageHeader, Tabs, Tag } from '@tocktix/pantry';
import { hasEngRoleSelector } from 'selectors/rolesSelectors';
import { useNavigate, useParams, useSearchParams } from 'react-router-v7';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import LegalEditor from './LegalEditor';
import isLegalEditorSelector from 'selectors/isLegalEditorSelector';

/**
 * Editor route for a single (app, type) legal page. A locale tab strip selects
 * which localized version is being edited; every app (Tock and JCE) is localized.
 */
const LegalEditorRoute: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const params = useParams<{ app: string; type: string }>();
  const [searchParams] = useSearchParams();
  const isLegalEditor = useSelector(isLegalEditorSelector);
  const isEng = useSelector(hasEngRoleSelector);

  const app = parseApp(params.app);
  const type = parseTypeSlug(params.type);

  // Deep-link params (e.g. from an approved review): land on the right locale with the approved
  // draft pre-selected so Publish is immediately visible.
  const deepLinkedLocale = searchParams.get('locale');
  const parsedDraftId = Number(searchParams.get('draftId'));
  const deepLinkedDraftId =
    Number.isFinite(parsedDraftId) && parsedDraftId > 0 ? parsedDraftId : null;

  const [locale, setLocale] = useState<string>(
    LOCALES.some((option) => option.value === deepLinkedLocale)
      ? (deepLinkedLocale as string)
      : DEFAULT_LOCALE
  );
  const [submitResult, setSubmitResult] = useState<'submitted' | 'published' | null>(null);

  if (!isEng && !isLegalEditor) {
    return (
      <Box padding={[400, 500]}>
        <PageHeader title="Legal Pages" />
        <Alert
          color="danger"
          message="You do not have access to this tool."
          data-testid="legal-editor-access-denied"
        />
      </Box>
    );
  }

  if (!type) {
    return (
      <Box padding={[400, 500]}>
        <PageHeader title="Legal Pages" />
        <Alert
          color="warning"
          message="Unknown legal page type."
          data-testid="legal-unknown-type"
        />
        <Box style={{ marginTop: 16 }}>
          <Button label="Back" variant="outlined" onClick={() => navigate('/legal')} />
        </Box>
      </Box>
    );
  }

  return (
    <Box padding={[400, 500]} display="flex" flexDirection="column" gap={300}>
      <PageHeader
        title={`${APP_LABELS[app]} — ${typeLabel(type)}`}
        description="Changes go through a draft → review → publish workflow."
      />

      <Box display="flex" gap={100} alignItems="center" data-testid="legal-context-badge">
        <Button
          label="← Back"
          variant="text"
          color="secondary"
          size="compact"
          onClick={() => navigate('/legal')}
        />
        <Tag label={APP_LABELS[app]} color={APP_TAG_COLORS[app]} size="sm" />
        <Tag label={`Locale: ${locale}`} color="secondary" size="sm" />
      </Box>

      {submitResult && (
        <Alert
          color="success"
          message={submitResult === 'submitted' ? 'Submitted for review.' : 'Published.'}
          data-testid="legal-editor-submit-success"
          dismissible={{ onDismiss: () => setSubmitResult(null), 'aria-label': 'Dismiss' }}
        />
      )}

      <Box data-testid="legal-locale-tabs">
        <Tabs
          accessibilityLabels={{ overflowMenuButton: 'More locales' }}
          selectedTabId={locale}
          onTabChange={(tabId) => {
            setLocale(tabId);
            setSubmitResult(null);
          }}
        >
          {LOCALES.map((option) => (
            <Tabs.Item key={option.value} id={option.value} label={option.label} />
          ))}
        </Tabs>
      </Box>

      <LegalEditor
        // Remount the editor when the context changes so internal state resets cleanly.
        key={`${appParam(app)}-${typeSlug(type)}-${locale}`}
        app={app}
        type={type}
        locale={locale}
        // Only honor the deep-linked draft while its locale is showing; switching the locale tab
        // drops the pre-selection (and the remount key above resets the editor cleanly).
        initialDraftId={locale === deepLinkedLocale ? deepLinkedDraftId : null}
        onSubmitForReviewSuccess={() => setSubmitResult('submitted')}
        onPublishSuccess={() => setSubmitResult('published')}
      />
    </Box>
  );
};

export default LegalEditorRoute;
